import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  Chip,
  CircularProgress,
  Fade,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Timer as TimerIcon,
  ArrowBack as BackIcon,
  Restaurant as RestaurantIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import AppIconButton from './ui/AppIconButton';
// import { useTheme } from '@mui/material/styles';

interface ImprovedGuestVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  vendorName?: string;
}

const ImprovedGuestVerificationModal: React.FC<ImprovedGuestVerificationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  vendorName = 'this restaurant'
}) => {
  const { requestGuestSession, verifyGuestSession } = useAuth();
  
  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [guestName, setGuestName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [sessionToken, setSessionToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Timer for resend functionality
  const [timeLeft, setTimeLeft] = useState(60);
  
  // Refs for OTP inputs
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const steps = ['Your Details', 'Verify Phone', 'Start Ordering'];

  // Timer effect
  useEffect(() => {
    if (activeStep === 1 && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [activeStep, timeLeft]);

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setActiveStep(0);
      setGuestName('');
      setPhoneNumber('');
      setVerificationCode(['', '', '', '', '', '']);
      setSessionToken('');
      setLoading(false);
      setError(null);
      setSuccess(null);
      setTimeLeft(60);
    }
  }, [isOpen]);

  // Format phone number as user types (Indian format)
  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    // Format as Indian number: XXXXX-XXXXX
    if (cleaned.length <= 5) {
      return cleaned;
    }
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    if (formatted.replace(/\D/g, '').length <= 10) {
      setPhoneNumber(formatted);
    }
  };

  const validateDetails = () => {
    if (!guestName.trim()) {
      setError('Please enter your name');
      return false;
    }
    if (guestName.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return false;
    }
    // 10 digits (IN mobiles) up to 15 (E.164 max — covers UK 07… / +44… numbers)
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10 || cleanPhone.length > 15) {
      setError('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handleRequestOTP = async () => {
    if (!validateDetails()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      const response = await requestGuestSession(cleanPhone, guestName);
      
      if (response.sessionToken) {
        setSessionToken(response.sessionToken);
        setActiveStep(1);
        setSuccess('Verification code sent to your phone!');
        setTimeLeft(60);
        
        // Focus first OTP input
        setTimeout(() => {
          otpRefs.current[0]?.focus();
        }, 100);
      }
    } catch (err) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    if (value.length > 1) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit if all digits entered
    if (newCode.every(digit => digit) && newCode.join('').length === 6) {
      handleVerifyOTP(newCode.join(''));
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...verificationCode];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    
    setVerificationCode(newCode);
    if (pastedData.length === 6) {
      handleVerifyOTP(pastedData);
    }
  };

  const handleVerifyOTP = async (code?: string) => {
    const otp = code || verificationCode.join('');
    if (otp.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await verifyGuestSession(sessionToken, otp);

      // If we get here, verification succeeded (function throws on error)
      setSuccess('Phone verified successfully!');
      setActiveStep(2);

      // Auto-close after showing success
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Invalid verification code');
      setVerificationCode(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      const response = await requestGuestSession(cleanPhone, guestName);
      
      if (response.sessionToken) {
        setSessionToken(response.sessionToken);
        setSuccess('New verification code sent!');
        setTimeLeft(60);
        setVerificationCode(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch (err) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Fade in timeout={500}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    bgcolor: 'primary.light',
                    color: 'primary.main'
                  }}
                >
                  <RestaurantIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight="600">
                  Welcome to {vendorName}!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Let's get your details to start ordering
                </Typography>
              </Box>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Your Name"
                  value={guestName}
                  onChange={(e) => {
                    setGuestName(e.target.value);
                    setError(null);
                  }}
                  placeholder="Enter your name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  error={!!error && !guestName}
                  helperText={error && !guestName ? error : 'How should we address you?'}
                />

                <TextField
                  fullWidth
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="98765 43210"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" sx={{ mr: 0.5 }} />
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                          +91
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  error={!!error && !phoneNumber}
                  helperText={error && !phoneNumber ? error : 'We\'ll send a verification code via WhatsApp'}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleRequestOTP}
                  disabled={loading || !guestName || !phoneNumber}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </Button>
              </Stack>

              <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <LockIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                  Your information is secure and will only be used for order management
                </Typography>
              </Box>
            </Box>
          </Fade>
        );

      case 1:
        return (
          <Fade in timeout={500}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    bgcolor: 'success.light',
                    color: 'success.main'
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight="600">
                  Verify Your Phone
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We sent a 6-digit code to
                </Typography>
                <Chip 
                  label={phoneNumber} 
                  icon={<PhoneIcon />} 
                  sx={{ mt: 1 }}
                />
              </Box>

              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                  Enter verification code
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  {verificationCode.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={el => otpRefs.current[index] = el}
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(index, e)}
                      onPaste={index === 0 ? handleOTPPaste : undefined}
                      inputProps={{
                        maxLength: 1,
                        style: { 
                          textAlign: 'center', 
                          fontSize: '1.5rem',
                          fontWeight: 600
                        },
                      }}
                      sx={{ 
                        width: 50,
                        '& .MuiOutlinedInput-root': {
                          height: 56
                        }
                      }}
                      disabled={loading}
                    />
                  ))}
                </Box>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleVerifyOTP()}
                disabled={loading || verificationCode.some(d => !d)}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Verifying...' : 'Verify Phone'}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                {timeLeft > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    <TimerIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Resend code in {timeLeft}s
                  </Typography>
                ) : (
                  <Button
                    variant="text"
                    onClick={handleResendOTP}
                    disabled={loading}
                  >
                    Resend Code
                  </Button>
                )}
              </Box>

              <Button
                startIcon={<BackIcon />}
                onClick={() => setActiveStep(0)}
                sx={{ mt: 2 }}
                disabled={loading}
              >
                Change Phone Number
              </Button>
            </Box>
          </Fade>
        );

      case 2:
        return (
          <Fade in timeout={500}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Avatar 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  mx: 'auto', 
                  mb: 3,
                  bgcolor: 'success.main',
                  color: 'white'
                }}
              >
                <CheckIcon sx={{ fontSize: 60 }} />
              </Avatar>
              <Typography variant="h4" gutterBottom fontWeight="600">
                Welcome, {guestName}!
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Your phone has been verified successfully
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can now start ordering...
              </Typography>
            </Box>
          </Fade>
        );
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Guest Checkout
          </Typography>
          <AppIconButton
            icon={<CloseIcon sx={{ fontSize: 20 }} />}
            tooltip="Close"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            size="small"
            data-testid="guest-verification-close"
          />
        </Box>
      </DialogTitle>

      <Box sx={{ px: 2, pt: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ pb: 3 }}>
        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};

export default ImprovedGuestVerificationModal;